import React, { useEffect, useId, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import EcxCalendarPopup from './EcxCalendarPopup';
import { formatDisplayDate } from './ecxDateUtils';

function EcxDatePicker({
    value = '',
    onChange,
    placeholder = 'Select date',
    disabled = false,
    clearable = true,
    size = 'sm',
    min,
    max,
    id,
    name,
    className = '',
}) {
    const generatedId = useId();
    const inputId = id || generatedId;
    const containerRef = useRef(null);
    const popupRef = useRef(null);
    const [isOpen, setIsOpen] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            return undefined;
        }

        const handleClickOutside = (event) => {
            const clickedInsideAnchor = containerRef.current?.contains(event.target);
            const clickedInsidePopup = popupRef.current?.contains(event.target);

            if (!clickedInsideAnchor && !clickedInsidePopup) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [isOpen]);

    const handleClear = (event) => {
        event.preventDefault();
        event.stopPropagation();
        onChange?.('');
        setIsOpen(false);
    };

    const toggleOpen = () => {
        if (disabled) {
            return;
        }

        setIsOpen((open) => !open);
    };

    const handleSelect = (nextValue) => {
        onChange?.(nextValue);
    };

    return (
        <div
            ref={containerRef}
            className={`ecx-date-picker ecx-date-picker--${size} ${
                value ? 'has-value' : ''
            } ${isOpen ? 'is-open' : ''} ${disabled ? 'is-disabled' : ''} ${className}`.trim()}
        >
            <span className="ecx-date-picker__icon" aria-hidden="true">
                <i className="bi bi-calendar3"></i>
            </span>

            <div className="ecx-date-picker__field">
                <button
                    type="button"
                    className="ecx-date-picker__open"
                    onClick={toggleOpen}
                    disabled={disabled}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}
                    aria-labelledby={`${inputId}-display`}
                >
                    <span
                        className={`ecx-date-picker__display ${value ? '' : 'is-placeholder'}`}
                        id={`${inputId}-display`}
                    >
                        {value ? formatDisplayDate(value) : placeholder}
                    </span>
                </button>

                <div className="ecx-date-picker__actions">
                    {clearable && value && !disabled && (
                        <button
                            type="button"
                            className="ecx-date-picker__clear"
                            onClick={handleClear}
                            aria-label="Clear date"
                            title="Clear date"
                        >
                            <i className="bi bi-x-lg"></i>
                        </button>
                    )}
                    <button
                        type="button"
                        className="ecx-date-picker__trigger"
                        onClick={toggleOpen}
                        disabled={disabled}
                        aria-label="Open calendar"
                        title="Open calendar"
                    >
                        <i className={`bi ${isOpen ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                    </button>
                </div>
            </div>

            {name && <input type="hidden" name={name} value={value} />}

            {isOpen && !disabled && createPortal(
                <div ref={popupRef}>
                    <EcxCalendarPopup
                        value={value}
                        onSelect={handleSelect}
                        onClose={() => setIsOpen(false)}
                        min={min}
                        max={max}
                        anchorRef={containerRef}
                    />
                </div>,
                document.body
            )}
        </div>
    );
}

export function EcxDateRangePicker({
    startValue = '',
    endValue = '',
    onStartChange,
    onEndChange,
    startPlaceholder = 'From date',
    endPlaceholder = 'To date',
    disabled = false,
    size = 'sm',
    className = '',
}) {
    return (
        <div className={`ecx-date-range-picker ${className}`.trim()}>
            <div className="ecx-date-range-picker__item">
                <EcxDatePicker
                    value={startValue}
                    onChange={onStartChange}
                    placeholder={startPlaceholder}
                    disabled={disabled}
                    size={size}
                    max={endValue || undefined}
                />
            </div>

            <span className="ecx-date-range-picker__separator" aria-hidden="true">
                <i className="bi bi-arrow-right"></i>
            </span>

            <div className="ecx-date-range-picker__item">
                <EcxDatePicker
                    value={endValue}
                    onChange={onEndChange}
                    placeholder={endPlaceholder}
                    disabled={disabled}
                    size={size}
                    min={startValue || undefined}
                />
            </div>
        </div>
    );
}

export default EcxDatePicker;
